import type { DisciplineStageLog } from './interfaces'

import { DateFormat } from '@brighterly/lib-core-types/date/enums'

import dayjs from 'dayjs'

interface DisciplineStageLogLists {
  expectations: Record<string, string>
  channels: Record<string, string>
  reach: Record<string, string>
  reasons: Record<string, string>
  subReasons: Record<string, string>
}

interface DisciplineStageLogChange {
  from: string | null
  to: string | null
}

interface DisciplineStageLogSection {
  label: string
  from: string
  to: string
}

interface DisciplineStageLogComment {
  text: string
  channels: string[]
  reach: string | null
}

/** One log row, ready to draw: exactly one of the optional parts is set. */
export interface DisciplineStageLogView {
  label: string
  isAutomatic: boolean
  stages?: DisciplineStageLogChange
  priorities?: { from: number | null, to: number | null }
  change?: DisciplineStageLogChange
  sections?: DisciplineStageLogSection[]
  comment?: DisciplineStageLogComment
}

const COMMENT_LABELS: Record<string, string> = {
  COMMENT_ADDED: 'add comment',
  COMMENT_EDITED: 'edit comment',
  COMMENT_DELETED: 'delete comment',
}

const DEADLINE_UNASSIGNED = 'Deadline unassigned'
const AGENT_UNASSIGNED = 'Agent unassigned'

type LogObject = Record<string, unknown>

const isObject = (value: unknown): value is LogObject =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

const asText = (value: unknown): string | null => {
  if (value === null || value === undefined) return null
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value).trim() || null
  }
  return null
}

/** Older rows stored lists as `{0: …, 1: …}` objects, so both shapes read as a list. */
const asList = (value: unknown): string[] => {
  const items = Array.isArray(value) ? value : isObject(value) ? Object.values(value) : []
  return items.map(asText).filter((item): item is string => item !== null)
}

const hasContent = (value: unknown): boolean =>
  (Array.isArray(value) && value.length > 0) || (isObject(value) && Object.keys(value).length > 0)

const asNumber = (value: unknown): number | null => {
  const text = asText(value)
  return text !== null && !Number.isNaN(Number(text)) ? Number(text) : null
}

const formatDate = (value: unknown): string | null => {
  const text = asText(value)
  return text && dayjs(text).isValid() ? dayjs(text).format(DateFormat.KanbanDate) : null
}

const labelled = (codes: string[], labels: Record<string, string>): string[] => codes.map(code => labels[code] ?? code)

const getUpdateAction = (oldValue: unknown, value: unknown): 'add' | 'delete' | 'update' => {
  if (!hasContent(oldValue) && hasContent(value)) return 'add'
  if (hasContent(oldValue) && !hasContent(value)) return 'delete'
  return 'update'
}

const buildFailDetails = (value: unknown, lists: DisciplineStageLogLists): string | null => {
  if (!isObject(value)) return null

  const lines: [string, string | null][] = [
    ['Reasons:', labelled(asList(value.reasons), lists.reasons).join(', ') || null],
    ['Sub reasons:', labelled(asList(value.sub_reasons), lists.subReasons).join(', ') || null],
    ['Note:', asText(value.note)],
    ['Amount:', asText(value.amount)],
    ['Date:', formatDate(value.date)],
    ['Type:', asText(value.type)],
  ]
  const text = lines.filter(([, line]) => line).map(([label, line]) => `${label} ${line}`).join('\n')

  return text || null
}

const scheduleLines = (value: unknown): string[] => {
  const items = Array.isArray(value) ? value : isObject(value) ? Object.values(value) : []
  return items.filter(isObject).map(item => `${asText(item.day) ?? ''}: ${asText(item.from) ?? ''} - ${asText(item.to) ?? ''}`)
}

/** The legacy row listed only the activation parts that changed (the customer schedule always). */
const buildActivationSections = (
  oldValue: unknown,
  value: unknown,
  lists: DisciplineStageLogLists,
): DisciplineStageLogSection[] => {
  const before = isObject(oldValue) ? oldValue : {}
  const after = isObject(value) ? value : {}
  const beforeDetails = isObject(before.details) ? before.details : {}
  const afterDetails = isObject(after.details) ? after.details : {}

  const pairs: [string, string, string, boolean][] = [
    ['Final schedule (agent):', asText(before.agent_schedule) ?? '', asText(after.agent_schedule) ?? '', false],
    [
      'Expected results:',
      labelled(asList(beforeDetails.expectations), lists.expectations).join('; '),
      labelled(asList(afterDetails.expectations), lists.expectations).join('; '),
      false,
    ],
    ['Preferences:', asText(beforeDetails.preferences) ?? '', asText(afterDetails.preferences) ?? '', false],
    ['Customer schedule:', scheduleLines(before.schedule).join('\n'), scheduleLines(after.schedule).join('\n'), true],
    ['Start date:', asText(before.start_date) ?? '', asText(after.start_date) ?? '', false],
  ]

  return pairs
    .filter(([, from, to, always]) => (from || to) && (always || from !== to))
    .map(([label, from, to]) => ({ label, from, to }))
}

export const getStageLogActorName = (log: DisciplineStageLog): string | null =>
  log.actor?.name ?? log.actor?.full_name ?? null

export const buildDisciplineStageLogView = (log: DisciplineStageLog, lists: DisciplineStageLogLists): DisciplineStageLogView => {
  const isAutomatic = !log.actor
  const commentLabel = COMMENT_LABELS[log.action]

  if (commentLabel) {
    const value = isObject(log.value) ? log.value : {}
    return {
      label: commentLabel,
      isAutomatic: false,
      comment: {
        text: asText(value.comment) ?? '',
        channels: labelled(asList(value.channel), lists.channels),
        reach: asText(value.reach) ? lists.reach[String(value.reach)] ?? String(value.reach) : null,
      },
    }
  }

  switch (log.field) {
    case 'stage':
      return { label: 'change stage', isAutomatic, stages: { from: asText(log.old_value), to: asText(log.value) } }
    case 'stage_deadline':
      return {
        label: 'change deadline',
        isAutomatic: false,
        change: {
          from: formatDate(log.old_value) ?? DEADLINE_UNASSIGNED,
          to: formatDate(log.value) ?? DEADLINE_UNASSIGNED,
        },
      }
    case 'agent_id':
      return {
        label: 'change agent',
        isAutomatic: false,
        change: { from: log.old_agent?.name ?? AGENT_UNASSIGNED, to: log.new_agent?.name ?? AGENT_UNASSIGNED },
      }
    case 'kanban_priority':
      return { label: 'change priority', isAutomatic, priorities: { from: asNumber(log.old_value), to: asNumber(log.value) } }
    case 'expectations': {
      const action = getUpdateAction(log.old_value, log.value)
      return {
        label: `${action} expectations`,
        isAutomatic: false,
        change: {
          from: action === 'update' ? asList(log.old_value).join(', ') : null,
          to: asList(log.value).join(', '),
        },
      }
    }
    case 'stage_details': {
      const action = getUpdateAction(log.old_value, log.value) === 'add' ? 'add' : 'update'
      return {
        label: `${action} fail details`,
        isAutomatic: false,
        change: {
          from: action === 'update' ? buildFailDetails(log.old_value, lists) : null,
          to: buildFailDetails(log.value, lists),
        },
      }
    }
    case 'activation_data':
      return {
        label: 'change activation data',
        isAutomatic: false,
        sections: buildActivationSections(log.old_value, log.value, lists),
      }
    default:
      return {
        label: log.field ? `change ${log.field.replace(/_/g, ' ')}` : '',
        isAutomatic: false,
        change: { from: asText(log.old_value), to: asText(log.value) },
      }
  }
}

/** A stage badge opens every run of rows written while the discipline sat in the same stage. */
export const isStageLogGroupStart = (logs: DisciplineStageLog[], index: number): boolean =>
  index === 0 || logs[index - 1]?.stage !== logs[index]?.stage

export const isStageLogGroupEnd = (logs: DisciplineStageLog[], index: number): boolean =>
  index >= logs.length - 1 || logs[index + 1]?.stage !== logs[index]?.stage
